import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Loader2, 
  Rocket, 
  Code, 
  FlaskConical,
  Plus,
  Book,
  ChevronRight,
  Github,
  ExternalLink,
  Mail,
  RefreshCw,
  Terminal,
  FileText,
  Settings,
  Database,
  Calendar,
  HardDrive,
  ChevronLeft,
  Trash2,
  Play,
  Square,
  AlertTriangle,
  Copy,
  Check,
  Key,
  Download,
  Cog,
  Globe,
  GitBranch,
  Eye,
  EyeOff,
  Server,
  Search,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ShellTab } from '@/components/shell/ShellTab';
import { LogsTab } from '@/components/logs/LogsTab';
import { BackupsTab } from '@/components/backups/BackupsTab';
import { 
  getBranches, 
  getBranchHistory, 
  getMailpitToken,
  getBranchConfig, 
  saveBranchConfig,
  getBranchBackupSchedules,
  createBranchBackupSchedule,
  getDeploymentSettings,
  manageDeploymentService,
  updateDeploymentSettings,
  deleteDeployment,
  updateDeploymentDomain,
  updateCloudflareProxy,
  changeOdooVersion,
  checkDomainAvailability,
  getPostgresAccess,
  togglePostgresAccess,
  managePostgresWhitelist,
  getCodeServerToken,
  checkUserSshKeys,
  getSshTunnel,
  createSshTunnel,
  getDeploymentFormData,
  createDeployment,
  getTestResults,
  getOdooUsers,
  connectAsOdooUser,
  rebuildDeployment,
  type BranchBackupSchedule,
  type DeploymentFormData,
} from '@/api/deployments';
import type { SshTunnelInfo } from '@/types/deployments';
import { 
  toggleBackupSchedule, 
  runBackupScheduleNow, 
  deleteBackupSchedule,
  editBackupSchedule,
} from '@/api/project';
import type { BranchWithBuild, BranchType, BranchesApiResponse, CommitWithBuild, LatestBuild, TestResult } from '@/types/deployments';
import { getStatusIndicator } from '@/types/deployments';
import { useDeploymentSocket, type BranchSocketShape, type BuildSocketShape } from '@/hooks/useDeploymentSocket';

type IncomingBranchPayload = BranchSocketShape;
type IncomingBuildPayload = BuildSocketShape;

const normalizeIncomingBuild = (build?: IncomingBuildPayload): LatestBuild | null => {
  if (!build || typeof build.id !== 'number') {
    return null;
  }

  const baseStatus = build.status || '';
  const resolvedStatus =
    (!baseStatus || baseStatus.toLowerCase() === 'success') && build.heartbeat_status
      ? build.heartbeat_status
      : baseStatus || 'queued';

  return {
    id: build.id,
    status: resolvedStatus,
    fqdn: build.fqdn ?? null,
    created_at: build.created_at ?? build.created ?? null,
    active: build.active ?? false,
    container_name: build.container_name ?? null,
    db_name: build.db_name ?? null,
    server_id: build.server_id ?? null,
    mailpit_fqdn: build.mailpit_fqdn ?? null,
    code_server_enabled: build.code_server_enabled ?? false,
  };
};

const normalizeIncomingBranch = (branch: IncomingBranchPayload): BranchWithBuild | null => {
  if (!branch || typeof branch.id !== 'number') {
    return null;
  }

  const branchType = (branch.branch_type ?? 'development').toLowerCase() as BranchType;
  const latest_build = normalizeIncomingBuild(branch.latest_build);

  return {
    id: branch.id,
    name: branch.name ?? `Branch ${branch.id}`,
    branch_type: branchType,
    latest_build,
  };
};

const normalizeIncomingBranches = (branches: IncomingBranchPayload[]): BranchWithBuild[] => {
  return branches
    .map(normalizeIncomingBranch)
    .filter((b): b is BranchWithBuild => Boolean(b));
};

const getBuildTimestamp = (build?: LatestBuild | null) => {
  if (!build) return -Infinity;
  const ts = build.created_at ?? (build as any).created ?? null;
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

const sortBranchesByLatest = (branches: BranchWithBuild[]) =>
  [...branches].sort((a, b) => getBuildTimestamp(b.latest_build) - getBuildTimestamp(a.latest_build));

// Status indicator component
function StatusIndicator({ status, className }: { status: string; className?: string }) {
  const indicator = getStatusIndicator(status);
  
  const baseClasses = "inline-block w-3 h-3 rounded-full";
  
  const indicatorClasses: Record<string, string> = {
    'solid-green': 'bg-green-500',
    'solid-grey': 'bg-gray-400',
    'solid-gray': 'bg-gray-400',
    'solid-red': 'bg-red-500',
    'solid-yellow': 'bg-yellow-500',
    'blinking-green': 'border-2 border-green-500 border-t-transparent animate-spin',
    'blinking-blue': 'border-2 border-blue-500 border-t-transparent animate-spin',
    'blinking-yellow': 'border-2 border-yellow-500 border-t-transparent animate-spin',
    'blinking-red': 'border-2 border-red-500 border-t-transparent animate-spin',
  };
  
  return (
    <span className={cn(baseClasses, indicatorClasses[indicator] || 'bg-gray-400', className)} />
  );
}

// Branch type section header with icon
function BranchTypeHeader({ 
  type, 
  count,
  onAdd,
  canAdd = true,
}: { 
  type: BranchType; 
  count: number;
  onAdd?: () => void;
  canAdd?: boolean;
}) {
  const icons: Record<BranchType, React.ElementType> = {
    production: Rocket,
    staging: Code,
    development: FlaskConical,
  };
  
  const styles: Record<BranchType, { dot: string; text: string }> = {
    production: { dot: 'bg-violet-500', text: 'text-violet-700' },
    staging: { dot: 'bg-amber-500', text: 'text-amber-700' },
    development: { dot: 'bg-blue-500', text: 'text-blue-700' },
  };
  
  const Icon = icons[type];
  const style = styles[type];
  const title = type.charAt(0).toUpperCase() + type.slice(1);
  
  return (
    <div className="flex items-center justify-between px-2 py-1.5">
      <div className="flex items-center gap-2">
        <span className={cn("h-2.5 w-2.5 rounded-full", style.dot)} />
        <Icon className={cn("h-4 w-4", style.text)} />
        <span className="text-[13px] font-semibold">{title}</span>
        <Badge variant="outline" className="h-6 px-2 text-[11px]">
          {count}
        </Badge>
      </div>
      {canAdd && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-primary"
          onClick={onAdd}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Branch list item
function BranchItem({ 
  branch, 
  isActive, 
  onClick,
}: { 
  branch: BranchWithBuild; 
  isActive: boolean;
  onClick: () => void;
}) {
  // Subtle left border color based on branch type when active
  const activeBorderColors: Record<string, string> = {
    production: 'border-l-red-500',
    staging: 'border-l-amber-500',
    development: 'border-l-blue-500',
  };
  
  const activeBackgroundColors: Record<string, string> = {
    production: 'bg-red-50/50',
    staging: 'bg-amber-50/50',
    development: 'bg-blue-50/50',
  };
  
  const activeBorder = activeBorderColors[branch.branch_type] || 'border-l-green-600';
  const activeBg = activeBackgroundColors[branch.branch_type] || 'bg-green-50';
  
  return (
    <button
      className={cn(
        "group w-full text-left rounded-md border border-transparent transition-colors px-3 py-2 flex items-center gap-2.5",
        isActive 
          ? cn(activeBg, "border-primary/40", activeBorder)
          : "hover:border-border hover:bg-muted/60"
      )}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{branch.name}</p>
      </div>
      <div className="flex items-center gap-2">
        {branch.latest_build && (
          <StatusIndicator status={branch.latest_build.status} />
        )}
        <ChevronRight className={cn(
          "h-4 w-4 transition-colors",
          isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"
        )} />
      </div>
    </button>
  );
}

// Branch sidebar
function BranchSidebar({ 
  branches, 
  selectedBranchId,
  onSelectBranch,
  onAddDeployment,
  hasActiveSubscription = true,
  developerBranchesEnabled = true,
}: { 
  branches: BranchWithBuild[];
  selectedBranchId: number | null;
  onSelectBranch: (branchId: number) => void;
  onAddDeployment: (type: BranchType) => void;
  hasActiveSubscription?: boolean;
  developerBranchesEnabled?: boolean;
}) {
  const branchTypes: BranchType[] = ['production', 'staging', 'development'];
  const [searchTerm, setSearchTerm] = useState('');
  
  const branchesByType = useMemo(() => branchTypes.reduce((acc, type) => {
    acc[type] = branches.filter(b => b.branch_type === type);
    return acc;
  }, {} as Record<BranchType, BranchWithBuild[]>), [branches]);

  const filteredBranchesByType = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return branchesByType;

    return branchTypes.reduce((acc, type) => {
      acc[type] = branchesByType[type].filter(branch =>
        branch.name.toLowerCase().includes(term)
      );
      return acc;
    }, {} as Record<BranchType, BranchWithBuild[]>);
  }, [branchesByType, searchTerm]);

  const totalBranches = branches.length;
  const hasProduction = (branchesByType['production'] || []).length > 0;

  return (
    <div className="w-64 bg-white border-r border-border/70 flex flex-col h-full min-h-[400px]">
      {/* Header */}
      <div className="p-2.5 border-b border-border/70 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Branches</span>
          <Badge variant="secondary" className="text-[11px] px-2 h-5">
            {totalBranches}
          </Badge>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search"
            className="pl-9 h-8 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Branch list - scrollable */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-2">
        {branchTypes.map((type) => (
          <div key={type} className="space-y-1">
            <BranchTypeHeader 
              type={type} 
              count={filteredBranchesByType[type].length}
              onAdd={() => onAddDeployment(type)}
              canAdd={
                hasActiveSubscription && 
                (type !== 'development' || developerBranchesEnabled) &&
                (type !== 'production' || !hasProduction)
              }
            />
            
            {/* Development branches disabled message */}
            {type === 'development' && !developerBranchesEnabled && hasActiveSubscription && (
              <div className="px-2.5 py-1.5 text-[11px] text-muted-foreground border rounded-md bg-muted/50">
                Development builds require the Developer Branches & CI add-on.{' '}
                <a href="#" className="text-primary hover:underline">Enable</a>
              </div>
            )}
            
            {filteredBranchesByType[type].map((branch) => (
              <BranchItem
                key={branch.id}
                branch={branch}
                isActive={selectedBranchId === branch.id}
                onClick={() => onSelectBranch(branch.id)}
              />
            ))}
            
            {filteredBranchesByType[type].length === 0 && (
              <div className="px-2.5 py-2 text-[11px] text-muted-foreground border border-dashed rounded-md bg-white">
                {searchTerm ? 'No matches' : `No ${type} deployments`}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Footer - docs button */}
      <div className="p-3 border-t border-border/70 bg-white">
        <Button
          variant="outline"
          className="w-full justify-center gap-2"
          asChild
        >
          <a href="https://docs.doploy.io" target="_blank" rel="noopener noreferrer">
            <Book className="h-4 w-4" />
            Documentation
          </a>
        </Button>
      </div>
    </div>
  );
}


// Commit entry component for history tab
function CommitEntry({ 
  item, 
  branchType,
  githubRepo,
  isFirst,
  isLast,
  onOpenTests,
  onOpenConnect,
  onOpenCodeServer,
  onRestart,
  onOpenRemoteIde,
  onRebuild,
  codeServerFeatureEnabled,
  codeServerLoadingId,
  restartLoadingId,
  rebuildLoadingId,
}: { 
  item: CommitWithBuild;
  branchType: string;
  githubRepo: string | null;
  isFirst: boolean;
  isLast: boolean;
  onOpenTests: (buildId?: number | null, buildStatus?: string | null) => void;
  onOpenConnect: (buildId?: number | null, buildFqdn?: string | null) => void;
  onOpenCodeServer: (buildId?: number | null) => void;
  onRestart: (buildId?: number | null) => void;
  onOpenRemoteIde: (buildId?: number | null) => void;
  onRebuild: (buildId?: number | null) => void;
  codeServerFeatureEnabled: boolean;
  codeServerLoadingId: number | null;
  restartLoadingId: number | null;
  rebuildLoadingId: number | null;
}) {
  const { commit, build } = item;
  const status = build?.status?.toLowerCase() || '';
  const isBuilding = ['building', 'updating', 'restoring', 'queued'].includes(status);
  const isDevelopment = branchType === 'development';
  const isStaging = branchType === 'staging';
  const [isMailLoading, setIsMailLoading] = useState(false);
  const startTimestamp = useMemo(() => {
    if (!build?.started_at) return null;
    const timestamp = new Date(build.started_at).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }, [build?.started_at]);
  const [elapsedBuildTime, setElapsedBuildTime] = useState<string | null>(null);
  
  useEffect(() => {
    if (!isBuilding || !startTimestamp) {
      setElapsedBuildTime(null);
      return;
    }
    
    const updateElapsed = () => {
      const elapsedMs = Math.max(0, Date.now() - startTimestamp);
      const totalSeconds = Math.floor(elapsedMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      
      const formatted = hours > 0
        ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        : `${minutes}:${String(seconds).padStart(2, '0')}`;
      
      setElapsedBuildTime(formatted);
    };
    
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [isBuilding, startTimestamp]);
  const isCodeServerLoading = codeServerLoadingId === build?.id;
  const isRestarting = restartLoadingId === build?.id;
  const isRebuilding = rebuildLoadingId === build?.id;
  const betaAvailable =
    codeServerFeatureEnabled &&
    build?.active &&
    (isDevelopment || isStaging);
  const hasCodeServer =
    betaAvailable &&
    !!build?.code_server_enabled &&
    !!build?.code_server_fqdn &&
    !isBuilding;

  const handleMailClick = async () => {
    if (!build?.id || !build.mailpit_fqdn || isMailLoading) return;

    setIsMailLoading(true);

    try {
      const data = await getMailpitToken(build.id);

      if (!data?.token || !data?.login_url) {
        throw new Error('Missing login data');
      }

      // Post the token to the mailpit host so the cookie is set there
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.login_url;
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = data.token;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error(error);
      toast.error('Unable to open Mail. Please try again.');
    } finally {
      setIsMailLoading(false);
    }
  };
  
  return (
    <div className="flex min-w-0">
      {/* Git Tree Visualization - Simple neutral style like Sourcetree/GitHub */}
      <div className="flex flex-col items-center w-5 shrink-0 mr-3">
        {/* Line above node */}
        <div className={cn(
          "w-0.5 flex-1 min-h-3",
          isFirst ? "bg-transparent" : "bg-gray-300"
        )} />
        
        {/* Commit node - simple gray circle like git tools */}
        <div className="w-2.5 h-2.5 rounded-full bg-gray-500 shrink-0 z-10" />
        
        {/* Line below node */}
        <div className={cn(
          "w-0.5 flex-1 min-h-3",
          isLast ? "bg-transparent" : "bg-gray-300"
        )} />
      </div>
      
      {/* Commit Card */}
      <div className="flex-1 bg-white p-3 rounded-lg border shadow-sm mb-3 min-w-0 break-words">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 w-full">
          {/* Commit Info */}
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center gap-2 mb-1">
              {commit.commit_hash && githubRepo && (
                <a 
                  href={`https://github.com/${githubRepo}/commit/${commit.commit_hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center"
                >
                  <Badge variant="default" className="bg-primary hover:bg-primary/90">
                    <Github className="h-3 w-3 mr-1" />
                    #{commit.commit_hash.slice(0, 7)}
                  </Badge>
                </a>
              )}
              {build?.odoo_version && (
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
                  Odoo {build.odoo_version}
                </Badge>
              )}
            </div>
            
            <p className="font-medium text-sm mb-1 truncate">
              {commit.message || 'No message'}
            </p>
            
            <div className="text-xs text-gray-500 space-y-0.5">
              <div>
                By <strong>{commit.author || 'Unknown'}</strong>
              </div>
              {commit.timestamp && (
                <div>{new Date(commit.timestamp).toLocaleString(undefined, { 
                  year: 'numeric', 
                  month: 'numeric', 
                  day: 'numeric', 
                  hour: 'numeric', 
                  minute: 'numeric', 
                  second: 'numeric',
                  timeZoneName: 'short' 
                })}</div>
              )}
              {build?.container_name && (
                <div>Container: <strong>{build.container_name}</strong></div>
              )}
              {isDevelopment && isBuilding && build?.started_at && (
                <div>
                  Elapsed:{' '}
                  <strong className="font-mono text-primary-600">
                    {elapsedBuildTime || 'calculating...'}
                  </strong>
                </div>
              )}
              {build?.build_time && isDevelopment && (
                <div>Build time: <strong>{build.build_time}</strong></div>
              )}
              {isDevelopment && build?.status && !isBuilding && (
                <div>
                  Test results:{' '}
                  <strong className={cn(
                    status === 'success' && 'text-green-600',
                    (status === 'error' || status === 'active error') && 'text-red-600',
                    status === 'warning' && 'text-yellow-600'
                  )}>
                    {status === 'success' && '✅ Success'}
                    {(status === 'error' || status === 'active error') && '❌ Failed'}
                    {status === 'warning' && '⚠️ Warning'}
                  </strong>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
            {build?.fqdn && build.active ? (
              <>
                {/* Status Indicator */}
                <div className="flex items-center gap-1.5 text-sm mr-2">
                  <span className="text-gray-500">Status:</span>
                  <StatusIndicator status={build.status || 'unknown'} />
                  <span className="capitalize">{build.status}</span>
                </div>
                
                {/* Connect Button */}
                <Button variant="outline" size="sm" asChild>
                  <a href={`https://${build.fqdn}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Connect
                  </a>
                </Button>
                
                {/* Connect As Button */}
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={isDevelopment && isBuilding}
                  onClick={() => onOpenConnect(build?.id, build?.fqdn || null)}
                >
                  Connect As...
                </Button>
                
                {/* Mail Button (for dev/staging only) */}
                {(isDevelopment || isStaging) && build.mailpit_fqdn && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={(isDevelopment && isBuilding) || isMailLoading}
                    onClick={handleMailClick}
                  >
                    {isMailLoading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <Mail className="h-3.5 w-3.5 mr-1" />
                        Mail
                      </>
                    )}
                  </Button>
                )}
                
                {/* Tests Button (dev only) */}
                {isDevelopment && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-300"
                    disabled={!build?.id}
                    onClick={() => onOpenTests(build?.id, build?.status)}
                  >
                    <FlaskConical className="h-3.5 w-3.5 mr-1" />
                    Tests
                  </Button>
                )}

                {/* VS Code / Restart / Remote IDE (Beta) */}
                {betaAvailable && (
                  <div className="xl:ml-auto flex flex-col items-stretch xl:items-end gap-2 w-full xl:w-36">
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      Beta
                    </Badge>
                    <div className="flex flex-col items-stretch gap-2 w-full">
                      {hasCodeServer && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="justify-start"
                          disabled={isCodeServerLoading}
                          onClick={() => onOpenCodeServer(build?.id)}
                        >
                          {isCodeServerLoading ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              Opening...
                            </>
                          ) : (
                            <>
                              <Code className="h-3.5 w-3.5 mr-1" />
                              VS Code
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-start text-amber-700 border-amber-200"
                        disabled={isBuilding || isRestarting}
                        onClick={() => onRestart(build?.id)}
                      >
                        {isRestarting ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            Restarting...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            Restart Odoo
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="justify-start"
                        disabled={isBuilding}
                        onClick={() => onOpenRemoteIde(build?.id)}
                      >
                        <Terminal className="h-3.5 w-3.5 mr-1" />
                        Remote IDE
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Rebuild Button (dev only, first item, not building) */}
                {isDevelopment && isFirst && !isBuilding && build?.id && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isRebuilding}
                    onClick={() => onRebuild(build?.id)}
                  >
                    {isRebuilding ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Rebuilding...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Rebuild
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" disabled>
                  No Build Available
                </Button>
                {isDevelopment && isFirst && !isBuilding && build?.id && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isRebuilding}
                    onClick={() => onRebuild(build?.id)}
                  >
                    {isRebuilding ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Rebuilding...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Rebuild
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Test Results Modal
function TestResultsModal({
  open,
  onOpenChange,
  projectId,
  branchId,
  buildId,
  buildStatus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  branchId: number;
  buildId: number | null;
  buildStatus: string | null;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [levelFilter, setLevelFilter] = useState<'all' | 'error' | 'warning' | 'info' | 'success'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;

    if (open && buildId) {
      setIsLoading(true);
      setError(null);
      getTestResults(projectId, branchId, buildId)
        .then((data) => {
          if (!isMounted) return;
          if (data.success) {
            setResults(data.test_results || []);
            setIsBuilding(data.is_building);
          } else {
            setError(data.error || 'Failed to load test results');
          }
        })
        .catch(() => {
          if (isMounted) setError('Failed to load test results');
        })
        .finally(() => {
          if (isMounted) setIsLoading(false);
        });
    } else if (!open) {
      setResults([]);
      setSearchTerm('');
      setLevelFilter('all');
      setError(null);
      setIsBuilding(false);
    }

    return () => {
      isMounted = false;
    };
  }, [open, buildId, projectId, branchId]);

  const filteredResults = results.filter((r) => {
    const matchesLevel = levelFilter === 'all' || r.type?.toLowerCase().includes(levelFilter);
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch = !search || (r.message && r.message.toLowerCase().includes(search));
    return matchesLevel && matchesSearch;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[90vw] lg:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-blue-600" />
            Test Results
          </DialogTitle>
          <DialogDescription>
            {buildStatus ? `Build status: ${buildStatus}` : 'Recent test output for this build.'}
          </DialogDescription>
        </DialogHeader>

        {isBuilding && (
          <Alert className="bg-blue-50 border-blue-200 text-blue-900">
            <AlertTitle className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Tests are still running
            </AlertTitle>
            <AlertDescription>Refresh the modal to see new test results.</AlertDescription>
          </Alert>
        )}

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading test results...</span>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="md:w-52">
                <Label className="text-xs uppercase text-muted-foreground">Filter by Level</Label>
                <Select value={levelFilter} onValueChange={(v) => setLevelFilter(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="error">Errors Only</SelectItem>
                    <SelectItem value="warning">Warnings Only</SelectItem>
                    <SelectItem value="info">Info Only</SelectItem>
                    <SelectItem value="success">Success Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs uppercase text-muted-foreground">Search Messages</Label>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search in error messages..."
                  className="w-full"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                Showing {filteredResults.length} of {results.length} results
              </div>
            </div>

            <div className="border rounded-md max-h-[60vh] overflow-y-auto mt-3">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted">
                  <tr className="text-left">
                    <th className="p-3 w-40">Date</th>
                    <th className="p-3 w-28">Level</th>
                    <th className="p-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.length === 0 ? (
                    <tr>
                      <td className="p-3 text-center text-muted-foreground" colSpan={3}>
                        No test results found.
                      </td>
                    </tr>
                  ) : (
                    filteredResults.map((result, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3 align-top whitespace-nowrap text-xs text-muted-foreground">
                          {result.date}
                        </td>
                        <td className="p-3 align-top">
                          <Badge
                            variant="secondary"
                            className={cn(
                              'capitalize',
                              result.type?.includes('error') && 'bg-red-100 text-red-800',
                              result.type?.includes('warning') && 'bg-yellow-100 text-yellow-800',
                              result.type?.includes('info') && 'bg-blue-100 text-blue-800',
                              result.type?.includes('success') && 'bg-green-100 text-green-800'
                            )}
                          >
                            {result.type || 'info'}
                          </Badge>
                        </td>
                        <td className="p-3 align-top">
                          <pre className="whitespace-pre-wrap text-[11px] leading-tight bg-transparent border-0 p-0">
                            {result.message}
                          </pre>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type OdooUser = { id: string; name: string };

// Connect As... modal for Odoo logins
function ConnectAsModal({
  open,
  onOpenChange,
  projectId,
  buildId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  buildId: number | null;
}) {
  const [users, setUsers] = useState<OdooUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && buildId) {
      setError(null);
      setSelectedUserId('');
      setUsers([]);
      setIsLoading(true);
      getOdooUsers(projectId, buildId)
        .then((data) => {
          if (data.success && data.users) {
            const sorted = [...data.users].sort((a, b) => a.name.localeCompare(b.name));
            setUsers(sorted);
            if (!sorted.length) {
              setError('No users found for this deployment.');
            }
          } else {
            setError(data.error || 'Failed to load users.');
          }
        })
        .catch(() => setError('Failed to load users.'))
        .finally(() => setIsLoading(false));
    } else if (!open) {
      setSelectedUserId('');
      setUsers([]);
      setError(null);
    }
  }, [open, buildId, projectId]);

  const handleConnect = async () => {
    if (!buildId || !selectedUserId) return;
    setIsSubmitting(true);
    try {
      const data = await connectAsOdooUser(projectId, buildId, selectedUserId);
      if (data.success && data.auth_url) {
        window.open(data.auth_url, '_blank', 'noopener,noreferrer');
        onOpenChange(false);
      } else {
        toast.error(data.error || 'Failed to connect as user.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to connect as user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Login as Odoo User</DialogTitle>
          <DialogDescription>
            Select an Odoo user for this deployment. A new tab will open to the selected environment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert className="bg-yellow-50 border-yellow-200 text-yellow-900">
            <AlertTitle>Pop-ups required</AlertTitle>
            <AlertDescription className="text-sm text-yellow-900">
              Enable pop-ups for this site so the login tab can open.
            </AlertDescription>
          </Alert>

          <div className="flex items-center justify-between gap-2">
            <Label>Select user</Label>
          </div>
          <Select
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            disabled={isLoading || !users.length}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={isLoading ? 'Loading users...' : 'Choose a user'} />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={isSubmitting || !selectedUserId || !!error}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Connecting, please wait...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Remote IDE modal for SSH tunnel + VS Code handoff
function RemoteIdeModal({
  open,
  onOpenChange,
  buildId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildId: number | null;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasKeys, setHasKeys] = useState<boolean | null>(null);
  const [tunnel, setTunnel] = useState<SshTunnelInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<'personal' | 'generate' | null>(null);
  const [copied, setCopied] = useState<'cmd' | 'key' | null>(null);

  const resetState = useCallback(() => {
    setIsLoading(false);
    setHasKeys(null);
    setTunnel(null);
    setError(null);
    setActionLoading(null);
    setCopied(null);
  }, []);

  useEffect(() => {
    if (open && buildId) {
      setIsLoading(true);
      setError(null);
      setTunnel(null);
      setCopied(null);
      checkUserSshKeys()
        .then((keys) => {
          setHasKeys(!!keys?.has_keys);
          if (keys?.has_keys) {
            return getSshTunnel(buildId);
          }
          return null;
        })
        .then((tunnelResp) => {
          if (tunnelResp && tunnelResp.success && tunnelResp.tunnel) {
            setTunnel(tunnelResp.tunnel);
          }
        })
        .catch(() => {
          const userMessage = 'Failed to load Remote IDE details. Please try again.';
          setError(userMessage);
        })
        .finally(() => setIsLoading(false));
    } else if (!open) {
      resetState();
    }
  }, [open, buildId, resetState]);

  const handleCopy = async (value: string, which: 'cmd' | 'key') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch (err) {
      toast.error('Unable to copy to clipboard.');
    }
  };

  const handleDownloadKey = () => {
    if (!tunnel?.private_key) return;
    const blob = new Blob([tunnel.private_key], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doploy_ssh_key';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerate = async (usePersonalKey: boolean) => {
    if (!buildId) return;
    setActionLoading(usePersonalKey ? 'personal' : 'generate');
    setError(null);
    try {
      const resp = await createSshTunnel(buildId, usePersonalKey);
      if (!resp.success || !resp.tunnel) {
        throw new Error(resp.error || 'Failed to create connection');
      }
      setTunnel(resp.tunnel);
      setHasKeys(true);
      toast.success('Connection details ready.');
    } catch (err: any) {
      const userMessage = 'Something went wrong. Please try again.';
      setError(userMessage);
      toast.error(userMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const showPrivateKey = !!tunnel?.private_key && tunnel.use_personal_key !== true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Remote IDE</DialogTitle>
          <DialogDescription>
            Generate connection details or use your personal SSH key to access this deployment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading Remote IDE details...
            </div>
          ) : (
            <>
              {hasKeys === false && (
                <Alert className="bg-blue-50 border-blue-200 text-blue-900">
                  <AlertTitle>SSH key required for personal key option</AlertTitle>
                  <AlertDescription className="space-y-2 text-sm">
                    <p>Add an SSH public key in Settings → SSH Keys to use your personal key.</p>
                    <Button asChild variant="outline" size="sm">
                      <a href="/settings/ssh-keys" target="_blank" rel="noreferrer">
                        Add SSH Key
                      </a>
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="default"
                  size="sm"
                  disabled={!hasKeys || actionLoading !== null}
                  onClick={() => handleGenerate(true)}
                >
                  {actionLoading === 'personal' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      Use Personal Key
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading !== null}
                  onClick={() => handleGenerate(false)}
                >
                  {actionLoading === 'generate' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Cog className="h-4 w-4 mr-2" />
                      Generate New Connection
                    </>
                  )}
                </Button>
              </div>

              {tunnel && (
                <div className="rounded-md border p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground">Host</div>
                      <div className="font-mono text-sm">{tunnel.host}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Port</div>
                      <div className="font-mono text-sm">{tunnel.port}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">User</div>
                      <div className="font-mono text-sm">{tunnel.username || 'odoo'}</div>
                    </div>
                  </div>

                  {(tunnel.vs_code_command || tunnel.vs_code_command_simple) && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">VS Code Quick Connect</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={tunnel.vs_code_command || tunnel.vs_code_command_simple || ''}
                          className="font-mono text-xs"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleCopy(
                              tunnel.vs_code_command || tunnel.vs_code_command_simple || '',
                              'cmd'
                            )
                          }
                        >
                          {copied === 'cmd' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Paste this into VS Code Remote-SSH to open directly in <code>/home/odoo/src</code>.
                      </p>
                    </div>
                  )}

                  {showPrivateKey && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Generated SSH Private Key</Label>
                      <Textarea
                        readOnly
                        value={tunnel.private_key || ''}
                        className="font-mono text-xs"
                        rows={8}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(tunnel.private_key || '', 'key')}
                        >
                          {copied === 'key' ? (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-1" />
                              Copy Key
                            </>
                          )}
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleDownloadKey}>
                          <Download className="h-4 w-4 mr-1" />
                          Download Key
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Save as <code>~/.ssh/doploy_key</code> and set permissions with <code>chmod 600</code>.
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Connection details expire after 24 hours. Regenerate anytime to refresh access.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// History Tab Content
function HistoryTab({ 
  projectId, 
  branchId,
  branchType,
  githubRepo,
}: { 
  projectId: string;
  branchId: number;
  branchType: string;
  githubRepo: string | null;
}) {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const [codeServerLoadingId, setCodeServerLoadingId] = useState<number | null>(null);
  const [restartLoadingId, setRestartLoadingId] = useState<number | null>(null);
  const [rebuildLoadingId, setRebuildLoadingId] = useState<number | null>(null);
  const [rebuildTargetId, setRebuildTargetId] = useState<number | null>(null);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [remoteIdeBuildId, setRemoteIdeBuildId] = useState<number | null>(null);
  const [remoteIdeOpen, setRemoteIdeOpen] = useState(false);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['branch-history', projectId, branchId, page],
    queryFn: () => getBranchHistory(projectId, branchId, page),
    enabled: !!projectId && !!branchId,
  });
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testBuildId, setTestBuildId] = useState<number | null>(null);
  const [testBuildStatus, setTestBuildStatus] = useState<string | null>(null);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [connectBuildId, setConnectBuildId] = useState<number | null>(null);

  const handleOpenTests = (buildId?: number | null, status?: string | null) => {
    if (!buildId) return;
    setTestBuildId(buildId);
    setTestBuildStatus(status || null);
    setIsTestModalOpen(true);
  };

  const handleOpenConnect = (buildId?: number | null) => {
    if (!buildId) return;
    setConnectBuildId(buildId);
    setIsConnectModalOpen(true);
  };

  const handleOpenCodeServer = async (buildId?: number | null) => {
    if (!buildId) return;
    setCodeServerLoadingId(buildId);
    try {
      const data = await getCodeServerToken(buildId);

      if (!data?.token || !data?.login_url) {
        throw new Error('Missing code-server token response');
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.login_url;
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = data.token;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Unable to open VS Code. Please try again.');
    } finally {
      setCodeServerLoadingId(null);
    }
  };

  const handleRestart = async (buildId?: number | null) => {
    if (!buildId) return;
    setRestartLoadingId(buildId);
    try {
      const result = await manageDeploymentService(projectId, branchId, 'restart');
      if (!result.success) {
        throw new Error(result.error || 'Failed to restart Odoo');
      }
      toast.success('Odoo restart has been requested.');
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to restart Odoo');
    } finally {
      setRestartLoadingId(null);
    }
  };

  const handleRebuild = async (buildId?: number | null) => {
    if (!buildId) return;
    setRebuildLoadingId(buildId);
    try {
      const result = await rebuildDeployment(projectId, buildId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to rebuild deployment');
      }
      toast.success(result.message || 'Rebuild started.');
      queryClient.invalidateQueries({ queryKey: ['branch-history', projectId, branchId] });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to rebuild deployment');
    } finally {
      setRebuildLoadingId(null);
    }
  };

  const handleRebuildRequest = (buildId?: number | null) => {
    if (!buildId) return;
    setRebuildTargetId(buildId);
    setRebuildConfirmOpen(true);
  };

  const handleConfirmRebuild = async () => {
    if (!rebuildTargetId) return;
    setRebuildConfirmOpen(false);
    await handleRebuild(rebuildTargetId);
    setRebuildTargetId(null);
  };

  const rebuildModalLoading = rebuildLoadingId === rebuildTargetId;

  const handleOpenRemoteIde = (buildId?: number | null) => {
    if (!buildId) return;
    setRemoteIdeBuildId(buildId);
    setRemoteIdeOpen(true);
  };

  const handleConnectModalChange = (open: boolean) => {
    setIsConnectModalOpen(open);
    if (!open) {
      setConnectBuildId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load commit history.</AlertDescription>
      </Alert>
    );
  }

  const commits = data?.commits || [];
  const pagination = data?.pagination;
  const codeServerFeatureEnabled = !!data?.branch?.project?.code_server_enabled;

  if (commits.length === 0) {
    return (
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertDescription className="text-yellow-800">
          No commits found for this branch.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="mt-4 w-full max-w-7xl mx-auto break-words min-w-0">
      {commits.map((item, index) => (
        <CommitEntry 
          key={item.commit.id} 
          item={item}
          branchType={branchType}
          githubRepo={githubRepo}
          isFirst={index === 0 && page === 1}
          isLast={index === commits.length - 1 && !pagination?.has_next}
          onOpenTests={handleOpenTests}
          onOpenConnect={handleOpenConnect}
          onOpenCodeServer={handleOpenCodeServer}
          onRestart={handleRestart}
          onOpenRemoteIde={handleOpenRemoteIde}
          codeServerFeatureEnabled={codeServerFeatureEnabled}
          codeServerLoadingId={codeServerLoadingId}
          restartLoadingId={restartLoadingId}
          onRebuild={handleRebuildRequest}
          rebuildLoadingId={rebuildLoadingId}
        />
      ))}
      
      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 ml-10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={!pagination.has_prev}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={!pagination.has_next}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      <TestResultsModal
        open={isTestModalOpen}
        onOpenChange={setIsTestModalOpen}
        projectId={projectId}
        branchId={branchId}
        buildId={testBuildId}
        buildStatus={testBuildStatus}
      />
      <ConnectAsModal
        open={isConnectModalOpen}
        onOpenChange={handleConnectModalChange}
        projectId={projectId}
        buildId={connectBuildId}
      />
      <RemoteIdeModal
        open={remoteIdeOpen}
        onOpenChange={(open) => {
          setRemoteIdeOpen(open);
          if (!open) {
            setRemoteIdeBuildId(null);
          }
        }}
        buildId={remoteIdeBuildId}
      />
      <Dialog open={rebuildConfirmOpen} onOpenChange={(open) => {
        setRebuildConfirmOpen(open);
        if (!open) setRebuildTargetId(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rebuild deployment?</DialogTitle>
            <DialogDescription>
              This will delete the current instance and create a new one. Service may be interrupted during rebuild.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRebuildConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRebuild}
              disabled={rebuildModalLoading}
            >
              {rebuildModalLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rebuilding...
                </>
              ) : (
                'Rebuild'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Config Tab - Odoo Configuration Editor
function ConfigTab({ 
  projectId, 
  branchId 
}: { 
  projectId: string; 
  branchId: number;
}) {
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [containerName, setContainerName] = useState<string>('');
  
  const hasChanges = content !== originalContent;

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getBranchConfig(projectId, branchId);
      if (result.success && result.content) {
        setContent(result.content);
        setOriginalContent(result.content);
        setContainerName(result.container_name || '');
      } else {
        setError(result.error || 'Failed to load configuration');
      }
    } catch {
      setError('Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, [projectId, branchId]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await saveBranchConfig(projectId, branchId, content);
      if (result.success) {
        setOriginalContent(content);
        setSuccessMessage(result.message || 'Configuration saved successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(result.error || 'Failed to save configuration');
      }
    } catch {
      setError('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReload = () => {
    loadConfig();
    setSuccessMessage(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-gray-500">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto space-y-4 break-words min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Odoo Configuration File</h3>
          {containerName && (
            <p className="text-sm text-gray-500">Container: {containerName}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReload} disabled={isSaving}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Settings className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Alert variant="destructive" className="py-2 px-3">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
      
      {successMessage && (
        <Alert className="bg-green-50 border-green-200 py-2 px-3">
          <AlertDescription className="text-sm text-green-700">{successMessage}</AlertDescription>
        </Alert>
      )}

      {hasChanges && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertDescription className="text-yellow-700">
            You have unsaved changes. Click "Save Changes" to apply them.
          </AlertDescription>
        </Alert>
      )}

      {/* Editor */}
      <div className="border rounded-lg overflow-hidden">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full h-[500px] p-4 font-mono text-sm bg-gray-50 focus:outline-none resize-none"
          spellCheck={false}
          placeholder="Configuration file content..."
        />
      </div>
    </div>
  );
}

// Backup Schedule Tab for branch
function BackupScheduleTab({ 
  projectId, 
  branchId 
}: { 
  projectId: string; 
  branchId: number;
}) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['branchBackupSchedules', projectId, branchId],
    queryFn: () => getBranchBackupSchedules(projectId, branchId),
    enabled: !!projectId && !!branchId,
  });

  const schedules = data?.schedules || [];
  const backupStorageEnabled = data?.backup_storage_enabled ?? false;
  const build = data?.build;

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!backupStorageEnabled) {
    return (
      <Alert className="bg-yellow-50 border-yellow-200">
        <Calendar className="h-4 w-4" />
        <AlertTitle>Backup Storage Add-on Required</AlertTitle>
        <AlertDescription>
          To use backup schedules, you need to enable the Backup Storage add-on in your project settings.
        </AlertDescription>
      </Alert>
    );
  }

  if (!build) {
    return (
      <Alert>
        <AlertTitle>No Active Deployment</AlertTitle>
        <AlertDescription>
          There is no active deployment for this branch. Deploy your branch first to configure backup schedules.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4 w-full max-w-7xl mx-auto break-words min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Backup Schedules</h3>
          <p className="text-sm text-gray-500">
            Deployment: {build.fqdn} ({build.db_name})
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateOpen(true)}
          disabled={schedules.length >= 3}
          title={schedules.length >= 3 ? 'Maximum of 3 schedules allowed per deployment' : ''}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Schedule
        </Button>
      </div>

      {schedules.length >= 3 && (
        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-700">
            Maximum of 3 backup schedules allowed per deployment.
          </AlertDescription>
        </Alert>
      )}

      {/* Schedules Table */}
      {schedules.length > 0 ? (
        <div className="border rounded-lg overflow-hidden min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">Name</th>
                  <th className="py-2 px-3 text-left font-medium">Frequency</th>
                  <th className="py-2 px-3 text-left font-medium">Next Run</th>
                  <th className="py-2 px-3 text-left font-medium">Last Run</th>
                  <th className="py-2 px-3 text-left font-medium">Retention</th>
                  <th className="py-2 px-3 text-left font-medium">Status</th>
                  <th className="py-2 px-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <BackupScheduleRow 
                    key={schedule.id} 
                    schedule={schedule} 
                    projectId={projectId}
                    onRefresh={handleRefresh}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">No backup schedules yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Create your first backup schedule to automate regular backups of this Odoo instance.
          </p>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Schedule
          </Button>
        </div>
      )}

      {/* Create Schedule Modal */}
      <CreateBranchScheduleModal
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        projectId={projectId}
        branchId={branchId}
        onSuccess={handleRefresh}
      />
    </div>
  );
}

// Backup Schedule Row Component
function BackupScheduleRow({
  schedule,
  projectId,
  onRefresh,
}: {
  schedule: BranchBackupSchedule;
  projectId: string;
  onRefresh: () => void;
}) {
  const [isRunning, setIsRunning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteBackups, setDeleteBackups] = useState(false);

  const formatFrequency = () => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    switch (schedule.frequency) {
      case 'hourly':
        return `Every ${schedule.hours_interval || 1} hour(s) at :${pad(schedule.minute)}`;
      case 'daily':
        return `Daily at ${schedule.hour}:${pad(schedule.minute)}`;
      case 'weekly':
        return `Weekly on ${days[schedule.day_of_week || 0]} at ${schedule.hour}:${pad(schedule.minute)}`;
      case 'monthly':
        return `Monthly on day ${schedule.day_of_month} at ${schedule.hour}:${pad(schedule.minute)}`;
      case 'custom':
        return schedule.cron_expression || 'Custom schedule';
      default:
        return schedule.frequency;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const formatted = date.toLocaleString();
    const tz = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    return `${formatted} ${tz}`;
  };

  const handleToggle = async (active: boolean) => {
    try {
      await toggleBackupSchedule(projectId, schedule.id, active);
      onRefresh();
    } catch {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleRunNow = async () => {
    setIsRunning(true);
    try {
      const result = await runBackupScheduleNow(projectId, schedule.id);
      if (result.success) {
        toast.success('Backup started successfully');
        onRefresh();
      } else {
        toast.error('Failed to run backup. Please try again.');
      }
    } catch {
      toast.error('Failed to run backup. Please try again.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteBackupSchedule(projectId, schedule.id, deleteBackups);
      if (result.success) {
        setIsDeleteOpen(false);
        onRefresh();
      } else {
        toast.error('Failed to delete schedule. Please try again.');
      }
    } catch {
      toast.error('Failed to delete schedule. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <tr className="border-b hover:bg-gray-50">
        <td className="py-2 px-3 font-medium break-words">{schedule.name}</td>
        <td className="py-2 px-3 break-words">{formatFrequency()}</td>
        <td className="py-2 px-3 text-gray-600 text-xs break-words">{formatDate(schedule.next_run)}</td>
        <td className="py-2 px-3 text-gray-600 text-xs break-words">{schedule.last_run ? formatDate(schedule.last_run) : 'Never'}</td>
        <td className="py-2 px-3 break-words">{schedule.retention_count} backups</td>
        <td className="py-2 px-3">
          <div className="flex items-center gap-2">
            <Switch 
              checked={schedule.active} 
              onCheckedChange={handleToggle}
            />
            <span className={cn(
              "text-sm",
              schedule.active ? "text-green-600" : "text-gray-500"
            )}>
              {schedule.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </td>
        <td className="py-2 px-3">
          <div className="flex items-center justify-end gap-1">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditOpen(true)}
              className="h-7 px-2"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRunNow}
              disabled={isRunning}
              className="h-7 px-2"
              title="Run Now"
            >
              {isRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsDeleteOpen(true)}
              className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </td>
      </tr>

      {/* Edit Dialog */}
      <EditBranchScheduleModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        projectId={projectId}
        schedule={schedule}
        onSuccess={onRefresh}
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={(open) => !isDeleting && setIsDeleteOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backup Schedule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{schedule.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="deleteBackups"
                checked={deleteBackups}
                onChange={(e) => setDeleteBackups(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="deleteBackups" className="text-sm">
                Also delete all backups created by this schedule
              </label>
            </div>
            {deleteBackups && (
              <p className="text-sm text-red-600 mt-2">
                Warning: This will permanently delete all backups associated with this schedule.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Create Branch Backup Schedule Modal
function CreateBranchScheduleModal({
  open,
  onOpenChange,
  projectId,
  branchId,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  branchId: number;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [retentionCount, setRetentionCount] = useState(7);
  const [hoursInterval, setHoursInterval] = useState(1);
  const [minute, setMinute] = useState(0);
  const [dailyTime, setDailyTime] = useState('03:00');
  const [weeklyTime, setWeeklyTime] = useState('03:00');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [monthlyTime, setMonthlyTime] = useState('03:00');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setFrequency('daily');
    setRetentionCount(7);
    setHoursInterval(1);
    setMinute(0);
    setDailyTime('03:00');
    setWeeklyTime('03:00');
    setDayOfWeek(0);
    setMonthlyTime('03:00');
    setDayOfMonth(1);
    setError(null);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Schedule name is required');
      return;
    }
    
    setIsCreating(true);
    setError(null);
    
    try {
      const result = await createBranchBackupSchedule(projectId, branchId, {
        name: name.trim(),
        frequency,
        retention_count: retentionCount,
        hours_interval: hoursInterval,
        minute,
        daily_time: dailyTime,
        weekly_time: weeklyTime,
        day_of_week: dayOfWeek,
        monthly_time: monthlyTime,
        day_of_month: dayOfMonth,
      });
      
      if (result.success) {
        resetForm();
        onOpenChange(false);
        onSuccess();
      } else {
        setError(result.error || 'Failed to create schedule');
      }
    } catch {
      setError('Failed to create schedule');
    } finally {
      setIsCreating(false);
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isCreating) { onOpenChange(isOpen); if (!isOpen) resetForm(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Backup Schedule</DialogTitle>
          <DialogDescription>
            Set up automated backups for this deployment.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-blue-700 text-sm">
              You can have up to 3 backup schedules per deployment.
            </AlertDescription>
          </Alert>
          
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Schedule Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Production Backup"
            />
          </div>
          
          {/* Frequency */}
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Hourly Options */}
          {frequency === 'hourly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Run Every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    value={hoursInterval}
                    onChange={(e) => setHoursInterval(parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">hours</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>At Minute</Label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value) || 0)}
                  className="w-20"
                />
                <p className="text-xs text-gray-500">0-59</p>
              </div>
            </div>
          )}
          
          {/* Daily Options */}
          {frequency === 'daily' && (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <Label>Time (UTC)</Label>
              <Input
                type="time"
                value={dailyTime}
                onChange={(e) => setDailyTime(e.target.value)}
              />
            </div>
          )}
          
          {/* Weekly Options */}
          {frequency === 'weekly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select value={dayOfWeek.toString()} onValueChange={(v) => setDayOfWeek(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {days.map((day, i) => (
                      <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Time (UTC)</Label>
                <Input
                  type="time"
                  value={weeklyTime}
                  onChange={(e) => setWeeklyTime(e.target.value)}
                />
              </div>
            </div>
          )}
          
          {/* Monthly Options */}
          {frequency === 'monthly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <Select value={dayOfMonth.toString()} onValueChange={(v) => setDayOfMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}{day > 28 ? ' (if exists)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Time (UTC)</Label>
                <Input
                  type="time"
                  value={monthlyTime}
                  onChange={(e) => setMonthlyTime(e.target.value)}
                />
              </div>
            </div>
          )}
          
          {/* Retention */}
          <div className="space-y-2">
            <Label>Retention Policy</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Keep last</span>
              <Input
                type="number"
                min={0}
                value={retentionCount}
                onChange={(e) => setRetentionCount(parseInt(e.target.value) || 0)}
                className="w-20"
              />
              <span className="text-sm text-gray-500">backups</span>
            </div>
            <p className="text-xs text-gray-500">Set to 0 for unlimited retention</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !name.trim()}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Schedule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditBranchScheduleModal({
  open,
  onOpenChange,
  projectId,
  schedule,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  schedule: BranchBackupSchedule;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(schedule.name);
  const [frequency, setFrequency] = useState(schedule.frequency);
  const [retentionCount, setRetentionCount] = useState(schedule.retention_count);
  const [hoursInterval, setHoursInterval] = useState(schedule.hours_interval || 1);
  const [minute, setMinute] = useState(schedule.minute || 0);
  const [dailyTime, setDailyTime] = useState(
    schedule.frequency === 'daily' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [weeklyTime, setWeeklyTime] = useState(
    schedule.frequency === 'weekly' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [dayOfWeek, setDayOfWeek] = useState(schedule.day_of_week || 0);
  const [monthlyTime, setMonthlyTime] = useState(
    schedule.frequency === 'monthly' && schedule.hour !== null
      ? `${schedule.hour.toString().padStart(2, '0')}:${(schedule.minute || 0).toString().padStart(2, '0')}`
      : '03:00'
  );
  const [dayOfMonth, setDayOfMonth] = useState(schedule.day_of_month || 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Schedule name is required');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await editBackupSchedule(projectId, schedule.id, {
        name: name.trim(),
        frequency,
        retention_count: retentionCount,
        hours_interval: hoursInterval,
        minute,
        daily_time: dailyTime,
        weekly_time: weeklyTime,
        day_of_week: dayOfWeek,
        monthly_time: monthlyTime,
        day_of_month: dayOfMonth,
      });
      
      if (result.success) {
        onOpenChange(false);
        onSuccess();
        toast.success('Schedule updated successfully');
      } else {
        setError(result.error || 'Failed to update schedule');
      }
    } catch {
      setError('Failed to update schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isSubmitting) onOpenChange(isOpen); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Backup Schedule</DialogTitle>
          <DialogDescription>
            Modify the settings for this backup schedule.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="edit-name">Schedule Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Daily Production Backup"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {frequency === 'hourly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Run Every</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    value={hoursInterval}
                    onChange={(e) => setHoursInterval(parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                  <span className="text-sm text-gray-500">hours</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>At Minute</Label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value) || 0)}
                  className="w-20"
                />
                <p className="text-xs text-gray-500">0-59</p>
              </div>
            </div>
          )}
          
          {frequency === 'daily' && (
            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <Label>Time (UTC)</Label>
              <Input
                type="time"
                value={dailyTime}
                onChange={(e) => setDailyTime(e.target.value)}
              />
            </div>
          )}
          
          {frequency === 'weekly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select value={dayOfWeek.toString()} onValueChange={(v) => setDayOfWeek(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {days.map((day, i) => (
                      <SelectItem key={i} value={i.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Time (UTC)</Label>
                <Input
                  type="time"
                  value={weeklyTime}
                  onChange={(e) => setWeeklyTime(e.target.value)}
                />
              </div>
            </div>
          )}
          
          {frequency === 'monthly' && (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <Label>Day of Month</Label>
                <Select value={dayOfMonth.toString()} onValueChange={(v) => setDayOfMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}{day > 28 ? ' (if exists)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Time (UTC)</Label>
                <Input
                  type="time"
                  value={monthlyTime}
                  onChange={(e) => setMonthlyTime(e.target.value)}
                />
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Retention Policy</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Keep last</span>
              <Input
                type="number"
                min={0}
                value={retentionCount}
                onChange={(e) => setRetentionCount(parseInt(e.target.value) || 0)}
                className="w-20"
              />
              <span className="text-sm text-gray-500">backups</span>
            </div>
            <p className="text-xs text-gray-500">Set to 0 for unlimited retention</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Settings Tab for deployment
function SettingsTab({ 
  projectId, 
  branchId,
  branchType,
}: { 
  projectId: string; 
  branchId: number;
  branchType: string;
}) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteGithubBranch, setDeleteGithubBranch] = useState(false);
  const [deleteBackups, setDeleteBackups] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [isUpdatingAutoUpdates, setIsUpdatingAutoUpdates] = useState(false);
  
  // Domain change state
  const [newDomain, setNewDomain] = useState('');
  const [cloudflareProxied, setCloudflareProxied] = useState(true);
  const [isDomainChecking, setIsDomainChecking] = useState(false);
  const [domainAvailability, setDomainAvailability] = useState<{available: boolean; reason?: string} | null>(null);
  const [isUpdatingDomain, setIsUpdatingDomain] = useState(false);
  const [isUpdatingProxy, setIsUpdatingProxy] = useState(false);
  
  // Odoo version change state
  const [selectedVersion, setSelectedVersion] = useState('');
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [showVersionConfirm, setShowVersionConfirm] = useState(false);
  
  const isDevelopment = branchType === 'development';
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['deploymentSettings', projectId, branchId],
    queryFn: () => getDeploymentSettings(projectId, branchId),
    enabled: !!projectId && !!branchId,
  });

  const deployment = data?.deployment;
  const branch = data?.branch;
  const project = data?.project;
  
  const currentOdooVersion = branch?.odoo_version || project?.odoo_version || '';

  const handleServiceAction = async (action: 'start' | 'stop' | 'restart') => {
    setActionLoading(action);
    try {
      const result = await manageDeploymentService(projectId, branchId, action);
      if (result.success) {
        toast.success(`Service ${action} initiated`);
        setTimeout(() => refetch(), 2000);
      } else {
        toast.error(`Failed to ${action} service. Please try again.`);
      }
    } catch {
      toast.error(`Failed to ${action} service. Please try again.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAutoUpdates = async (enabled: boolean) => {
    setIsUpdatingAutoUpdates(true);
    try {
      const result = await updateDeploymentSettings(projectId, branchId, {
        automatic_odoo_updates: enabled,
      });
      if (result.success) {
        toast.success(enabled ? 'Auto updates enabled' : 'Auto updates disabled');
        refetch();
      } else {
        toast.error('Failed to update settings. Please try again.');
      }
    } catch {
      toast.error('Failed to update settings. Please try again.');
    } finally {
      setIsUpdatingAutoUpdates(false);
    }
  };

  const handleCheckDomain = async () => {
    if (!newDomain.trim()) return;
    setIsDomainChecking(true);
    setDomainAvailability(null);
    try {
      const result = await checkDomainAvailability(projectId, newDomain.trim().toLowerCase());
      setDomainAvailability(result);
    } catch {
      setDomainAvailability({ available: false, reason: 'Unable to check availability' });
    } finally {
      setIsDomainChecking(false);
    }
  };

  const handleUpdateDomain = async () => {
    if (!newDomain.trim()) return;
    setIsUpdatingDomain(true);
    try {
      const result = await updateDeploymentDomain(projectId, branchId, newDomain.trim().toLowerCase(), cloudflareProxied);
      if (result.success) {
        toast.success('Domain update initiated. This may take a few minutes.');
        setNewDomain('');
        setDomainAvailability(null);
        refetch();
      } else {
        toast.error('Failed to update domain. Please try again.');
      }
    } catch {
      toast.error('Failed to update domain. Please try again.');
    } finally {
      setIsUpdatingDomain(false);
    }
  };

  const handleUpdateCloudflareProxy = async (enabled: boolean) => {
    setIsUpdatingProxy(true);
    try {
      const result = await updateCloudflareProxy(projectId, branchId, enabled);
      if (result.success) {
        toast.success(enabled ? 'Cloudflare proxy enabled' : 'Cloudflare proxy disabled');
        refetch();
      } else {
        toast.error('Failed to update proxy setting. Please try again.');
      }
    } catch {
      toast.error('Failed to update proxy setting. Please try again.');
    } finally {
      setIsUpdatingProxy(false);
    }
  };

  const handleChangeVersion = async () => {
    if (!selectedVersion) return;
    setIsChangingVersion(true);
    try {
      const result = await changeOdooVersion(projectId, branchId, selectedVersion);
      if (result.success) {
        toast.success('Odoo version change initiated. A new build will be created.');
        setShowVersionConfirm(false);
        setSelectedVersion('');
        refetch();
      } else {
        toast.error('Failed to change Odoo version. Please try again.');
      }
    } catch {
      toast.error('Failed to change Odoo version. Please try again.');
    } finally {
      setIsChangingVersion(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteDeployment(projectId, branchId, {
        delete_github_branch: deleteGithubBranch,
        delete_associated_backups: deleteBackups,
      });
      if (result.success) {
        setIsDeleteOpen(false);
        toast.success('Deployment deletion initiated');
        window.location.reload();
      } else {
        toast.error('Failed to delete deployment. Please try again.');
      }
    } catch {
      toast.error('Failed to delete deployment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!deployment) {
    return (
      <Alert>
        <AlertTitle>No Active Deployment</AlertTitle>
        <AlertDescription>
          There is no active deployment for this branch.
        </AlertDescription>
      </Alert>
    );
  }

  const heartbeatStatus = deployment.heartbeat_status?.toLowerCase() || 'unknown';
  const isDoploy = deployment.fqdn?.endsWith('.doploy.site');

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6 break-words min-w-0">
      {/* Service Controls */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Odoo Service Controls</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Control the Odoo service on this deployment.
            </p>
            <Badge 
              className={cn(
                heartbeatStatus === 'success' && 'bg-green-100 text-green-700',
                heartbeatStatus === 'stopped' && 'bg-gray-100 text-gray-700',
                heartbeatStatus === 'error' && 'bg-red-100 text-red-700',
                !['success', 'stopped', 'error'].includes(heartbeatStatus) && 'bg-gray-100 text-gray-700'
              )}
            >
              {heartbeatStatus === 'success' && <><Play className="h-3 w-3 mr-1" /> Running</>}
              {heartbeatStatus === 'stopped' && <><Square className="h-3 w-3 mr-1" /> Stopped</>}
              {heartbeatStatus === 'error' && <><AlertTriangle className="h-3 w-3 mr-1" /> Error</>}
              {!['success', 'stopped', 'error'].includes(heartbeatStatus) && 'Unknown'}
            </Badge>
          </div>
          <div className="flex gap-2">
            {heartbeatStatus !== 'success' && (
              <Button
                variant="outline"
                className="text-green-600 border-green-300 hover:bg-green-50"
                onClick={() => handleServiceAction('start')}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'start' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                Start
              </Button>
            )}
            {heartbeatStatus !== 'stopped' && (
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => handleServiceAction('stop')}
                disabled={actionLoading !== null}
              >
                {actionLoading === 'stop' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4 mr-1" />}
                Stop
              </Button>
            )}
            <Button
              variant="outline"
              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
              onClick={() => handleServiceAction('restart')}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'restart' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Restart
            </Button>
          </div>
        </div>
      </div>

      {/* Domain Settings - Only for non-development branches */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Domain Settings</h3>
        {isDevelopment ? (
          <Alert className="bg-blue-50 border-blue-200">
            <Globe className="h-4 w-4" />
            <AlertTitle>Domain Not Configurable</AlertTitle>
            <AlertDescription>
              Domain changes are not allowed for development deployments. Your current domain is <strong>{deployment.fqdn}</strong>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-500">Current Domain</Label>
              <Input value={deployment.fqdn || ''} disabled className="mt-1" />
            </div>
            
            <div>
              <Label className="text-sm text-gray-500">New Domain Name</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={newDomain}
                  onChange={(e) => {
                    setNewDomain(e.target.value);
                    setDomainAvailability(null);
                  }}
                  placeholder="Enter new domain name"
                  className="flex-1"
                />
                <Button 
                  variant="outline" 
                  onClick={handleCheckDomain}
                  disabled={!newDomain.trim() || isDomainChecking}
                >
                  {isDomainChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                </Button>
              </div>
              {domainAvailability && (
                <p className={cn("text-sm mt-1", domainAvailability.available ? "text-green-600" : "text-red-600")}>
                  {domainAvailability.available ? '✓ Domain is available' : `✗ ${domainAvailability.reason || 'Domain not available'}`}
                </p>
              )}
            </div>
            
            {newDomain.endsWith('.doploy.site') && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="cloudflareProxied"
                  checked={cloudflareProxied}
                  onChange={(e) => setCloudflareProxied(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="cloudflareProxied" className="text-sm">
                  Enable Cloudflare proxy (recommended)
                </label>
              </div>
            )}
            
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription>
                <p>If you're using a domain ending with <strong>.doploy.site</strong>, we'll automatically set up DNS records.</p>
                {deployment.server && (
                  <p className="mt-1">For custom domains, create an <strong>A record</strong> pointing to <strong>{deployment.server.ip_address}</strong></p>
                )}
              </AlertDescription>
            </Alert>
            
            <div className="flex justify-end">
              <Button 
                onClick={handleUpdateDomain}
                disabled={!newDomain.trim() || isUpdatingDomain || (domainAvailability !== null && !domainAvailability.available)}
              >
                {isUpdatingDomain ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating...</> : 'Update Domain'}
              </Button>
            </div>
            
            {/* Cloudflare proxy toggle for existing .doploy.site domains */}
            {isDoploy && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-2">Current Domain Proxy Settings</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Cloudflare Proxy</Label>
                    <p className="text-sm text-gray-500">Enable Cloudflare proxy for {deployment.fqdn}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUpdatingProxy && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Switch
                      checked={deployment.cloudflare_proxied || false}
                      onCheckedChange={handleUpdateCloudflareProxy}
                      disabled={isUpdatingProxy}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* General Settings */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">General Settings</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-gray-500">Branch</Label>
              <Input value={branch?.name || ''} disabled className="mt-1" />
            </div>
            <div>
              <Label className="text-sm text-gray-500">Database</Label>
              <Input value={deployment.db_name || ''} disabled className="mt-1" />
            </div>
            <div>
              <Label className="text-sm text-gray-500">Container</Label>
              <Input value={deployment.container_name || ''} disabled className="mt-1" />
            </div>
          </div>

          {!isDevelopment && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label className="font-medium">Automatic Odoo Updates</Label>
                <p className="text-sm text-gray-500">Enable weekly automatic Odoo updates</p>
              </div>
              <div className="flex items-center gap-2">
                {isUpdatingAutoUpdates && <Loader2 className="h-4 w-4 animate-spin" />}
                <Switch
                  checked={deployment.automatic_odoo_updates || false}
                  onCheckedChange={handleToggleAutoUpdates}
                  disabled={isUpdatingAutoUpdates}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Odoo Version - Only for development branches */}
      {isDevelopment && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Odoo Version</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-500">Current Odoo Version</Label>
              <Input value={currentOdooVersion} disabled className="mt-1" />
              <p className="text-sm text-gray-500 mt-1">
                {branch?.odoo_version 
                  ? 'This development branch uses a custom Odoo version.'
                  : `This development branch inherits the Odoo version from the project (${project?.odoo_version}).`
                }
              </p>
            </div>
            
            <div>
              <Label className="text-sm text-gray-500">Change Odoo Version</Label>
              <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a new version..." />
                </SelectTrigger>
                <SelectContent>
                  {['16.0', '17.0', '18.0', '19.0'].map((v) => (
                    <SelectItem 
                      key={v} 
                      value={v}
                      disabled={v === currentOdooVersion}
                    >
                      {v} {v === currentOdooVersion && '(current)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Alert variant="destructive" className="bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-800">Important Notice</AlertTitle>
              <AlertDescription className="text-yellow-700">
                <p className="mb-2">Changing the Odoo version will:</p>
                <ul className="list-disc ml-5">
                  <li>Delete all current builds for this development branch</li>
                  <li>Create a new build with the selected Odoo version</li>
                </ul>
              </AlertDescription>
            </Alert>
            
            <div className="flex justify-end">
              <Button 
                onClick={() => setShowVersionConfirm(true)}
                disabled={!selectedVersion || selectedVersion === currentOdooVersion}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Change Odoo Version
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Git Information */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4">Git Information</h3>
        <div className="space-y-4">
          {/* Odoo Core */}
          <div>
            <h4 className="font-medium mb-2">Odoo Core</h4>
            {deployment.odoo_git_version ? (
              <div className="flex items-center gap-2">
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                  {deployment.odoo_git_version.slice(0, 8)}
                </code>
                <Button variant="outline" size="sm" asChild>
                  <a 
                    href={`https://github.com/odoo/odoo/commit/${deployment.odoo_git_version}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Github className="h-3 w-3 mr-1" />
                    View Commit
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(deployment.odoo_git_version || '', 'odoo')}
                >
                  {copied === 'odoo' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            ) : (
              <span className="text-sm text-gray-500">Version information not available</span>
            )}
          </div>

          {/* Odoo Enterprise */}
          {deployment.odoo_enterprise && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Odoo Enterprise</h4>
              {deployment.odoo_enterprise_git_version ? (
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {deployment.odoo_enterprise_git_version.slice(0, 8)}
                  </code>
                  <Button variant="outline" size="sm" asChild>
                    <a 
                      href={`https://github.com/odoo/enterprise/commit/${deployment.odoo_enterprise_git_version}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Github className="h-3 w-3 mr-1" />
                      View Commit
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(deployment.odoo_enterprise_git_version || '', 'enterprise')}
                  >
                    {copied === 'enterprise' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              ) : (
                <span className="text-sm text-gray-500">Version information not available</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Server Information - Only for non-development branches */}
      {!isDevelopment && deployment.server && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">Server Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Server Name:</span>{' '}
              <span className="font-medium">{deployment.server.name}</span>
            </div>
            <div>
              <span className="text-gray-500">IP Address:</span>{' '}
              <span className="font-medium">{deployment.server.ip_address}</span>
            </div>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="border border-red-200 rounded-lg p-4 bg-red-50">
        <h3 className="text-lg font-semibold text-red-800 mb-4">Danger Zone</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-red-800">Delete this deployment</p>
            <p className="text-sm text-red-600">
              This will permanently delete the deployment and all associated data.
            </p>
          </div>
          <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Deployment
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={(open) => {
        if (!isDeleting) {
          setIsDeleteOpen(open);
          if (!open) setDeleteConfirmation('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              You are about to delete the deployment <strong>{deployment.fqdn}</strong>.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning!</AlertTitle>
            <AlertDescription>
              All data associated with this deployment will be permanently lost.
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3 py-4">
            {!isDevelopment && (
              <>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="deleteBackups"
                    checked={deleteBackups}
                    onChange={(e) => setDeleteBackups(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="deleteBackups" className="text-sm">
                    Also delete all associated backups for this deployment
                  </label>
                </div>
                
                {/* Branch name confirmation for staging/production */}
                <div className="pt-3 border-t">
                  <Label htmlFor="deleteConfirmation" className="text-sm font-medium">
                    Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">{branch?.name}</span> to confirm deletion:
                  </Label>
                  <Input
                    id="deleteConfirmation"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={branch?.name || ''}
                    className="mt-2"
                    autoComplete="off"
                  />
                </div>
              </>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="deleteGithub"
                checked={deleteGithubBranch}
                onChange={(e) => setDeleteGithubBranch(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="deleteGithub" className="text-sm">
                Also delete the GitHub branch <strong>"{branch?.name}"</strong>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={isDeleting || (!isDevelopment && deleteConfirmation !== branch?.name)}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version Change Confirmation Dialog */}
      <Dialog open={showVersionConfirm} onOpenChange={(open) => !isChangingVersion && setShowVersionConfirm(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Version Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to change the Odoo version to <strong>{selectedVersion}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive" className="bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-800">This will:</AlertTitle>
            <AlertDescription className="text-yellow-700">
              <ul className="list-disc ml-5">
                <li>Delete all current builds for this development branch</li>
                <li>Create a new build with Odoo version {selectedVersion}</li>
              </ul>
            </AlertDescription>
          </Alert>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionConfirm(false)} disabled={isChangingVersion}>
              Cancel
            </Button>
            <Button onClick={handleChangeVersion} disabled={isChangingVersion}>
              {isChangingVersion ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Changing Version...
                </>
              ) : (
                'Change Version'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// PostgreSQL Remote Access Tab
function PostgresTab({ 
  projectId, 
  branchId,
  branchType,
}: { 
  projectId: string; 
  branchId: number;
  branchType: string;
}) {
  const [newIp, setNewIp] = useState('');
  const [isToggling, setIsToggling] = useState(false);
  const [isAddingIp, setIsAddingIp] = useState(false);
  const [removingIp, setRemovingIp] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['postgresAccess', projectId, branchId],
    queryFn: () => getPostgresAccess(projectId, branchId),
    enabled: !!projectId && !!branchId,
    refetchInterval: (query) => {
      // Poll every 3 seconds while pending
      if (query.state.data?.postgres_status === 'pending') {
        return 3000;
      }
      return false;
    },
  });

  const isDevelopment = branchType === 'development';
  const connectionDetails = data?.connection_details;
  const connectionStringMasked = useMemo(() => {
    const cs = connectionDetails?.connection_string;
    if (!cs) return '';
    if (showPassword) return cs;
    // Mask password segment between ":" and "@"
    return cs.replace(/:(?:[^@/]+)@/, ':••••••••@');
  }, [connectionDetails?.connection_string, showPassword]);

  const handleToggle = async (enable: boolean) => {
    if (!enable) {
      setShowDisableConfirm(true);
      return;
    }
    
    setIsToggling(true);
    try {
      const result = await togglePostgresAccess(projectId, branchId, enable);
      if (result.success) {
        const message =
          result.message ||
          (result.postgres_status === 'pending'
            ? 'PostgreSQL remote access is being configured...'
            : 'PostgreSQL remote access enabled');
        toast.success(message);
        refetch();
      } else {
        toast.error(result.error || 'Failed to enable PostgreSQL access. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to enable PostgreSQL access. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const confirmDisable = async () => {
    setShowDisableConfirm(false);
    setIsToggling(true);
    try {
      const result = await togglePostgresAccess(projectId, branchId, false);
      if (result.success) {
        const message =
          result.message ||
          (result.postgres_status === 'pending'
            ? 'PostgreSQL remote access is being disabled...'
            : 'PostgreSQL remote access disabled');
        toast.success(message);
        refetch();
      } else {
        toast.error(result.error || 'Failed to disable PostgreSQL access. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to disable PostgreSQL access. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const handleAddIp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIp.trim()) return;
    
    // Validate IP format
    const ipv4Regex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    const cidrRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}\/([0-9]|[1-2][0-9]|3[0-2])$/;
    
    if (!ipv4Regex.test(newIp) && !cidrRegex.test(newIp)) {
      toast.error('Invalid IP address format. Use IPv4 (e.g., 192.168.1.1) or CIDR notation (e.g., 10.0.0.0/24)');
      return;
    }
    
    setIsAddingIp(true);
    try {
      const result = await managePostgresWhitelist(projectId, branchId, 'add', newIp.trim());
      if (result.success) {
        toast.success(result.message || 'IP address added to whitelist');
        setNewIp('');
        refetch();
      } else {
        toast.error(result.error || 'Failed to add IP address. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to add IP address. Please try again.');
    } finally {
      setIsAddingIp(false);
    }
  };

  const handleRemoveIp = async (ip: string) => {
    setRemovingIp(ip);
    try {
      const result = await managePostgresWhitelist(projectId, branchId, 'remove', ip);
      if (result.success) {
        toast.success(result.message || 'IP address removed from whitelist');
        refetch();
      } else {
        toast.error(result.error || 'Failed to remove IP address. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to remove IP address. Please try again.');
    } finally {
      setRemovingIp(null);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{data?.error || 'Failed to load PostgreSQL access settings'}</AlertDescription>
      </Alert>
    );
  }

  // Development branches can't use PostgreSQL remote access
  if (isDevelopment || data.is_development) {
    return (
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>PostgreSQL Remote Access Not Available</AlertTitle>
        <AlertDescription>
          <p>PostgreSQL remote access is not available for development branches. This feature is only available for production and staging deployments.</p>
          <p className="mt-2"><strong>Reason:</strong> Development branches are temporary and may be deleted at any time, making remote database access unreliable and potentially insecure.</p>
        </AlertDescription>
      </Alert>
    );
  }

  if (!data?.has_build) {
    return (
      <Alert>
        <AlertTitle>No Active Deployment</AlertTitle>
        <AlertDescription>
          There is no active deployment for this branch. Deploy your branch first to configure PostgreSQL remote access.
        </AlertDescription>
      </Alert>
    );
  }

  const isPending = data.postgres_status === 'pending';
  const isEnabled = data.postgres_enabled;
  const ipWhitelist = data.ip_whitelist || [];

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6 break-words min-w-0">
      {/* Info Banner */}
      <Alert className="bg-blue-50 border-blue-200">
        <Database className="h-4 w-4" />
        <AlertTitle className="text-blue-800">About PostgreSQL Remote Access</AlertTitle>
        <AlertDescription className="text-blue-700">
          <p>Remote access to PostgreSQL is disabled by default for security. When you enable it, we:</p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>Configure PostgreSQL to accept connections from specific IP addresses</li>
            <li>Generate a secure, unique user and password for database access</li>
            <li>Automatically configure the firewall to allow PostgreSQL traffic</li>
          </ul>
          <p className="mt-2"><strong>Note:</strong> For security reasons, we recommend using this feature only when necessary and disabling it when not in use.</p>
          <p className="mt-1"><strong>Important:</strong> Please be aware that these settings are not included in backups.</p>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-w-0">
        {/* Connection Status Card */}
        <div className="border rounded-lg bg-white overflow-hidden min-w-0 break-words">
          <div className="h-1 bg-primary" />
          <div className="p-4">
            <h4 className="font-semibold mb-4">Connection Status</h4>
            
            {isPending ? (
              <div className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertTitle>Processing...</AlertTitle>
                  <AlertDescription>
                    PostgreSQL remote access is being configured. This usually takes 30-60 seconds.
                    The page will update automatically when complete.
                  </AlertDescription>
                </Alert>
                <Button disabled className="w-full">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Configuring...
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "inline-block h-3 w-3 rounded-full",
                    isEnabled ? "bg-green-500" : "bg-red-500"
                  )} />
                  <span className="font-medium">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                
                <Button
                  onClick={() => handleToggle(!isEnabled)}
                  disabled={isToggling}
                  variant={isEnabled ? "destructive" : "default"}
                  className="w-full"
                >
                  {isToggling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isEnabled ? 'Disabling...' : 'Enabling...'}
                    </>
                  ) : (
                    isEnabled ? 'Disable Remote Access' : 'Enable Remote Access'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* IP Whitelist Card */}
        <div className="border rounded-lg bg-white overflow-hidden min-w-0 break-words">
          <div className="h-1 bg-primary" />
          <div className="p-4">
            <h4 className="font-semibold mb-4">IP Whitelist</h4>
            
            {isPending ? (
              <Alert className="bg-gray-50">
                <AlertTitle>Configuration In Progress</AlertTitle>
                <AlertDescription>
                  IP whitelist management will be available once PostgreSQL remote access is fully configured.
                </AlertDescription>
              </Alert>
            ) : !isEnabled ? (
              <Alert className="bg-gray-50">
                <AlertTitle>Remote Access Disabled</AlertTitle>
                <AlertDescription>
                  IP whitelist management is disabled until you enable PostgreSQL remote access.
                  Click the "Enable Remote Access" button to get started.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert className="bg-yellow-50 border-yellow-200 mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Security Notice</AlertTitle>
                  <AlertDescription>
                    You <strong>must</strong> add at least one IP address to the whitelist before you can connect.
                    No IP addresses are whitelisted by default for security reasons.
                  </AlertDescription>
                </Alert>

                <p className="text-sm text-gray-500 mb-3">Only connections from these IP addresses will be allowed:</p>
                
                {/* IP List */}
                <div className="space-y-2 mb-4">
                  {ipWhitelist.length > 0 ? (
                    ipWhitelist.map((ip) => (
                      <div key={ip} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                        <code className="text-sm">{ip}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveIp(ip)}
                          disabled={removingIp === ip}
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                        >
                          {removingIp === ip ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-400">
                      <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                      <p>No IP addresses whitelisted</p>
                    </div>
                  )}
                </div>

                {/* Add IP Form */}
                <form onSubmit={handleAddIp} className="flex gap-2">
                  <Input
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                    placeholder="Add IP (e.g., 192.168.1.1 or 10.0.0.0/24)"
                    className="flex-1"
                  />
                  <Button type="submit" disabled={isAddingIp || !newIp.trim()}>
                    {isAddingIp ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </form>
                <p className="text-xs text-gray-500 mt-2">You can add individual IPs or CIDR ranges (e.g., 192.168.1.0/24)</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Connection Details Card */}
      {isEnabled && connectionDetails && !isPending && (
        <div className="border rounded-lg bg-white overflow-hidden">
          <div className="h-1 bg-green-500" />
          <div className="p-4">
            <h4 className="font-semibold mb-4">Connection Details</h4>
            
            {ipWhitelist.length === 0 && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>⚠️ No IP addresses are whitelisted.</strong> You must add at least one IP address to the whitelist before you can connect.
                </AlertDescription>
              </Alert>
            )}

            <Alert className="bg-blue-50 border-blue-200 mb-4">
              <Database className="h-4 w-4" />
              <AlertTitle>Connection Information</AlertTitle>
              <AlertDescription>
                Use the direct server IP address for PostgreSQL connections. Make sure you have added your IP address to the whitelist.
              </AlertDescription>
            </Alert>

            {/* Connection Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div>
                <p className="text-xs text-gray-500 mb-1">Host</p>
                <div className="flex items-center gap-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{connectionDetails.host}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(connectionDetails.host, 'host')}
                  >
                    {copiedField === 'host' ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Port</p>
                <div className="flex items-center gap-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{connectionDetails.port}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(connectionDetails.port.toString(), 'port')}
                  >
                    {copiedField === 'port' ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Database</p>
                <div className="flex items-center gap-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{connectionDetails.database}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(connectionDetails.database, 'database')}
                  >
                    {copiedField === 'database' ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Username</p>
                <div className="flex items-center gap-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{connectionDetails.username}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(connectionDetails.username, 'username')}
                  >
                    {copiedField === 'username' ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Password</p>
                <div className="flex items-center gap-1">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                    {showPassword ? connectionDetails.password : '••••••••••••'}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <Check className="h-3 w-3" /> : <Code className="h-3 w-3" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => connectionDetails.password && copyToClipboard(connectionDetails.password, 'password')}
                  >
                    {copiedField === 'password' ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Connection String */}
            {connectionDetails.connection_string && (
              <div className="mb-4">
                <h5 className="text-sm font-medium mb-2">Connection String</h5>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-3 rounded overflow-x-auto text-sm">
                    <code>{connectionStringMasked}</code>
                  </pre>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => connectionDetails.connection_string && copyToClipboard(connectionDetails.connection_string, 'connection_string')}
                  >
                    {copiedField === 'connection_string' ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                    Copy
                  </Button>
                </div>
              </div>
            )}

            {/* PSQL Command */}
            {connectionDetails.psql_command && (
              <div>
                <h5 className="text-sm font-medium mb-2">Connect with psql command line</h5>
                <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-sm mb-2">
                  <code>{connectionDetails.psql_command}</code>
                </pre>
                <p className="text-xs text-gray-500 mb-2">You'll be prompted for the password</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => connectionDetails.psql_command && copyToClipboard(connectionDetails.psql_command, 'psql_command')}
                >
                  {copiedField === 'psql_command' ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                  Copy Command
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disable Confirmation Dialog */}
      <Dialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Disable</DialogTitle>
            <DialogDescription>
              Are you sure you want to disable PostgreSQL remote access? This will revoke the remote user and remove all whitelisted IPs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDisable}>
              Disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main content area with tabs
function DeploymentContent({ 
  branch,
  projectId,
  githubRepo,
  activeTab,
  onTabChange,
}: { 
  branch: BranchWithBuild | null;
  projectId: string;
  githubRepo: string | null;
  activeTab: string;
  onTabChange: (tab: string) => void;
}) {
  if (!branch) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <Rocket className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Select a branch to view deployment details</p>
        </div>
      </div>
    );
  }
  
  const isDevelopment = branch.branch_type === 'development';
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <Tabs value={activeTab} onValueChange={onTabChange} className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="sticky top-0 z-10 bg-white border-b px-6 pt-6 pb-3">
          <h1 className="text-2xl font-bold mb-3">{branch.name}</h1>
          <TabsList className="mb-0">
            <TabsTrigger value="history" className="gap-1.5">
              <Github className="h-4 w-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="shell" className="gap-1.5">
              <Terminal className="h-4 w-4" />
              Shell
            </TabsTrigger>
            <TabsTrigger value="logs" className="gap-1.5">
              <FileText className="h-4 w-4" />
              Logs
            </TabsTrigger>
            <TabsTrigger value="config" className="gap-1.5">
              <Code className="h-4 w-4" />
              Config
            </TabsTrigger>
            {!isDevelopment && (
              <>
                <TabsTrigger value="backups" className="gap-1.5">
                  <HardDrive className="h-4 w-4" />
                  Backups
                </TabsTrigger>
                <TabsTrigger value="backup_scheduler" className="gap-1.5">
                  <Calendar className="h-4 w-4" />
                  Backup Schedule
                </TabsTrigger>
                <TabsTrigger value="postgres" className="gap-1.5">
                  <Database className="h-4 w-4" />
                  PostgreSQL
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="settings" className="gap-1.5">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4 min-w-0">
          <TabsContent value="history" className="mt-0">
            <HistoryTab 
              projectId={projectId}
              branchId={branch.id}
              branchType={branch.branch_type}
              githubRepo={githubRepo}
            />
          </TabsContent>
          
          <TabsContent value="shell" className="mt-0">
            <ShellTab projectId={projectId} branchId={branch.id} />
          </TabsContent>
          
          <TabsContent value="logs" className="mt-0">
            <LogsTab 
              projectId={projectId} 
              branchId={branch.id}
              branchType={branch.branch_type}
            />
          </TabsContent>
          
          <TabsContent value="config" className="mt-0">
            <ConfigTab projectId={projectId} branchId={branch.id} />
          </TabsContent>
          
          <TabsContent value="backups" className="mt-0">
            <BackupsTab projectId={projectId} branchId={branch.id} githubRepo={githubRepo} />
          </TabsContent>
          
          <TabsContent value="backup_scheduler" className="mt-0">
            <BackupScheduleTab projectId={projectId} branchId={branch.id} />
          </TabsContent>
          
          <TabsContent value="postgres" className="mt-0">
            <PostgresTab 
              projectId={projectId} 
              branchId={branch.id} 
              branchType={branch.branch_type}
            />
          </TabsContent>
          
          <TabsContent value="settings" className="mt-0">
            <SettingsTab 
              projectId={projectId} 
              branchId={branch.id}
              branchType={branch.branch_type}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// Deploy Odoo Instance Modal
function DeployInstanceModal({
  open,
  onOpenChange,
  projectId,
  onSuccess,
  initialBranchType = 'production',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  onSuccess: () => void;
  initialBranchType?: 'production' | 'staging' | 'development';
}) {
  const [formData, setFormData] = useState<DeploymentFormData | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [branchType, setBranchType] = useState<'production' | 'staging' | 'development'>(initialBranchType);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [selectedServerId, setSelectedServerId] = useState<string>('');
  const [domainName, setDomainName] = useState('');
  const [databaseName, setDatabaseName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createBackupSchedule, setCreateBackupSchedule] = useState(true);
  const [isFork, setIsFork] = useState(false);
  const [sourceBranchId, setSourceBranchId] = useState<string>('');
  const [newBranchName, setNewBranchName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Domain availability check
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState<{available: boolean; reason?: string} | null>(null);

  // Validation helpers
  const validateDatabaseName = (name: string): string | null => {
    if (!name.trim()) return null; // Don't show error for empty (will be caught on submit)
    if (!/^[a-zA-Z_]/.test(name)) return 'Must start with a letter or underscore';
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) return 'Only letters, numbers, and underscores allowed';
    return null;
  };

  const validateDomainName = (name: string): string | null => {
    if (!name.trim()) return null;
    // Basic domain validation - alphanumeric, hyphens, dots, no consecutive dots/hyphens
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(name)) {
      return 'Invalid domain format';
    }
    return null;
  };

  const databaseNameError = validateDatabaseName(databaseName);
  const domainNameError = validateDomainName(domainName);

  // Load form data when modal opens
  useEffect(() => {
    if (open && !formData) {
      setIsLoadingForm(true);
      getDeploymentFormData(projectId)
        .then((data) => {
          setFormData(data);
          if (data.default_domain) setDomainName(data.default_domain);
          if (data.default_database) setDatabaseName(data.default_database);
          if (data.default_password) setAdminPassword(data.default_password);
          setCreateBackupSchedule(data.backup_storage_enabled);
        })
        .catch(() => setError('Failed to load form data'))
        .finally(() => setIsLoadingForm(false));
    }
  }, [open, projectId, formData]);

  // Set branch type when modal opens with initial type, reset when closes
  useEffect(() => {
    if (open) {
      setBranchType(initialBranchType);
    } else {
      setSelectedBranchId('');
      setSelectedServerId('');
      setIsFork(false);
      setSourceBranchId('');
      setNewBranchName('');
      setError(null);
      setDomainAvailable(null);
    }
  }, [open, initialBranchType]);

  useEffect(() => {
    if (formData?.has_production && branchType === 'production') {
      setBranchType('staging');
    }
  }, [formData?.has_production, branchType]);

  // Check domain availability
  const handleCheckDomain = async () => {
    if (!domainName.trim()) return;
    setIsCheckingDomain(true);
    setDomainAvailable(null);
    try {
      const result = await checkDomainAvailability(projectId, domainName.trim().toLowerCase());
      setDomainAvailable(result);
    } catch {
      setDomainAvailable({ available: false, reason: 'Failed to check' });
    } finally {
      setIsCheckingDomain(false);
    }
  };

  // Generate new password
  const generatePassword = () => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    setAdminPassword(password);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!domainName.trim()) {
      setError('Domain name is required');
      return;
    }
    if (!databaseName.trim()) {
      setError('Database name is required');
      return;
    }
    if (branchType !== 'development' && !selectedServerId) {
      setError('Server selection is required');
      return;
    }
    if (isFork) {
      if (!sourceBranchId) {
        setError('Source branch is required for fork');
        return;
      }
      if (!newBranchName.trim()) {
        setError('New branch name is required for fork');
        return;
      }
    } else if (!selectedBranchId) {
      setError('Branch selection is required');
      return;
    }
    
    setIsCreating(true);
    try {
      const result = await createDeployment(projectId, {
        branch_type: branchType,
        branch_id: isFork ? undefined : parseInt(selectedBranchId),
        server_id: branchType === 'development' ? undefined : parseInt(selectedServerId),
        domain_name: domainName.trim().toLowerCase(),
        database_name: databaseName.trim(),
        admin_password: adminPassword,
        create_backup_schedule: branchType === 'production' && createBackupSchedule,
        is_fork: isFork,
        source_branch_id: isFork ? parseInt(sourceBranchId) : undefined,
        new_branch_name: isFork ? newBranchName.trim() : undefined,
      });
      
      if (result.success) {
        onOpenChange(false);
        onSuccess();
        toast.success('Deployment created! Your Odoo instance will be available shortly.');
      } else {
        setError('Unable to create deployment. Please check your settings and try again.');
      }
    } catch {
      setError('Unable to create deployment. Please try again later.');
    } finally {
      setIsCreating(false);
    }
  };

  // Get selected server warning
  const getServerWarning = () => {
    if (!selectedServerId || !formData) return null;
    const server = formData.servers.find(s => s.id.toString() === selectedServerId);
    if (!server) return null;
    
    if (branchType === 'staging' && server.has_production) {
      return 'This server is already running your production instance. We recommend using a separate server for staging.';
    }
    if (branchType === 'production' && server.has_staging) {
      return 'This server is already running staging instances. We recommend using a separate server for production.';
    }
    return null;
  };

  const serverWarning = getServerWarning();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isCreating && onOpenChange(isOpen)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Deploy Odoo Instance</DialogTitle>
          <DialogDescription>
            Create a new Odoo deployment for your project.
          </DialogDescription>
        </DialogHeader>
        
        {isLoadingForm ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !formData?.success ? (
          <Alert variant="destructive">
            <AlertDescription>Failed to load form data. Please try again.</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Branch Type Selection */}
            <div className="space-y-2">
              <Label>Deployment Type</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={branchType === 'production' ? 'default' : 'outline'}
                  className={cn(branchType === 'production' && 'ring-2 ring-primary')}
                  onClick={() => setBranchType('production')}
                  disabled={formData.has_production}
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Production
                </Button>
                <Button
                  type="button"
                  variant={branchType === 'staging' ? 'default' : 'outline'}
                  className={cn(branchType === 'staging' && 'ring-2 ring-primary')}
                  onClick={() => setBranchType('staging')}
                >
                  <Code className="h-4 w-4 mr-2" />
                  Staging
                </Button>
                <Button
                  type="button"
                  variant={branchType === 'development' ? 'default' : 'outline'}
                  className={cn(branchType === 'development' && 'ring-2 ring-primary')}
                  onClick={() => setBranchType('development')}
                  disabled={!formData.developer_branches_enabled}
                  title={!formData.developer_branches_enabled ? 'Developer Branches add-on required' : ''}
                >
                  <FlaskConical className="h-4 w-4 mr-2" />
                  Development
                </Button>
              </div>
              {formData.has_production && branchType !== 'production' && (
                <p className="text-xs text-gray-500">Production deployment already exists.</p>
              )}
              {!formData.developer_branches_enabled && (
                <p className="text-xs text-amber-600">Development builds require the Developer Branches & CI add-on.</p>
              )}
            </div>

            {/* Server Selection (not for development) */}
            {branchType !== 'development' && (
              <div className="space-y-2">
                <Label>Server</Label>
                {formData.servers.length > 0 ? (
                  <>
                    <Select value={selectedServerId} onValueChange={setSelectedServerId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a server" />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.servers.map((server) => (
                          <SelectItem key={server.id} value={server.id.toString()}>
                            <div className="flex items-center gap-2">
                              <Server className="h-4 w-4" />
                              {server.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {serverWarning && (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="text-yellow-700">{serverWarning}</AlertDescription>
                      </Alert>
                    )}
                  </>
                ) : formData.provisioning_servers > 0 ? (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Server className="h-4 w-4" />
                    <AlertTitle>Server Provisioning</AlertTitle>
                    <AlertDescription>
                      Your server is currently being provisioned. This typically takes 5-10 minutes.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Server className="h-4 w-4" />
                    <AlertTitle>No Servers Available</AlertTitle>
                    <AlertDescription>
                      You need to add a server before creating a production or staging deployment.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Development Server Info */}
            {branchType === 'development' && (
              <Alert className="bg-blue-50 border-blue-200">
                <Server className="h-4 w-4" />
                <AlertDescription>
                  Development builds are containerized and securely hosted on doploy managed servers.
                </AlertDescription>
              </Alert>
            )}

            {/* Fork Toggle */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isFork"
                checked={isFork}
                onChange={(e) => setIsFork(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="isFork" className="text-sm flex items-center gap-1">
                <GitBranch className="h-4 w-4" />
                Create a new branch (fork from existing)
              </label>
            </div>

            {/* Branch Selection or Fork Fields */}
            {isFork ? (
              <>
                <div className="space-y-2">
                  <Label>Source Branch</Label>
                  <Select value={sourceBranchId} onValueChange={setSourceBranchId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch to fork from" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* When forking, show ALL branches as source options */}
                      {formData.all_branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id.toString()}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>New Branch Name</Label>
                  <Input
                    value={newBranchName}
                    onChange={(e) => setNewBranchName(e.target.value)}
                    placeholder="feature-name"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Branch</Label>
                {/* Only show branches without active deployments */}
                {formData.available_branches.length === 0 ? (
                  <Alert className="bg-blue-50 border-blue-200">
                    <GitBranch className="h-4 w-4" />
                    <AlertDescription>
                      All branches already have active deployments. Use the "Create a new branch" option above to fork from an existing branch.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Select value={selectedBranchId} onValueChange={setSelectedBranchId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.available_branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id.toString()}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Domain Name */}
            <div className="space-y-2">
              <Label>Domain Name</Label>
              <div className="flex gap-2">
                <Input
                  value={domainName}
                  onChange={(e) => {
                    setDomainName(e.target.value);
                    setDomainAvailable(null);
                  }}
                  placeholder="your-domain.doploy.site"
                  className={cn("flex-1", domainNameError ? 'border-red-500' : '')}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCheckDomain}
                  disabled={isCheckingDomain || !domainName.trim() || !!domainNameError}
                >
                  {isCheckingDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
                </Button>
              </div>
              {domainNameError ? (
                <p className="text-xs text-red-600">{domainNameError}</p>
              ) : domainAvailable ? (
                <p className={cn("text-sm", domainAvailable.available ? "text-green-600" : "text-red-600")}>
                  {domainAvailable.available ? '✓ Domain available' : `✗ ${domainAvailable.reason || 'Not available'}`}
                </p>
              ) : null}
            </div>

            {/* Database Name */}
            <div className="space-y-2">
              <Label>Database Name</Label>
              <Input
                value={databaseName}
                onChange={(e) => setDatabaseName(e.target.value)}
                placeholder="database_name"
                className={databaseNameError ? 'border-red-500' : ''}
              />
              {databaseNameError ? (
                <p className="text-xs text-red-600">{databaseNameError}</p>
              ) : (
                <p className="text-xs text-gray-500">Letters, numbers, and underscores only. Must start with a letter.</p>
              )}
            </div>

            {/* Admin Password */}
            <div className="space-y-2">
              <Label>Admin Password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Admin password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button type="button" variant="outline" onClick={generatePassword}>
                  Generate
                </Button>
              </div>
            </div>

            {/* Backup Schedule Checkbox (Production only) */}
            {branchType === 'production' && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="createBackupSchedule"
                    checked={createBackupSchedule}
                    onChange={(e) => setCreateBackupSchedule(e.target.checked)}
                    disabled={!formData.backup_storage_enabled}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="createBackupSchedule" className="text-sm">
                    Create automatic daily backup schedule (7 day retention)
                  </label>
                </div>
                {!formData.backup_storage_enabled && (
                  <p className="text-xs text-amber-600">Backup storage add-on required.</p>
                )}
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating || isLoadingForm || !formData?.success || !!databaseNameError || !!domainNameError}>
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Deploy Instance'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeploymentsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [deployModalBranchType, setDeployModalBranchType] = useState<'production' | 'staging' | 'development'>('production');
  const queryClient = useQueryClient();
  
  // Get branch ID from URL, defaulting to null
  const branchIdFromUrl = searchParams.get('branch');
  const selectedBranchId = branchIdFromUrl ? parseInt(branchIdFromUrl, 10) : null;
  
  // Get active tab from URL, defaulting to 'history'
  const activeTabFromUrl = searchParams.get('tab') || 'history';
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId,
  });

  const handleBranchesUpdate = useCallback(
    (incomingBranches: BranchSocketShape[]) => {
      if (!projectId) return;

      const normalized = sortBranchesByLatest(normalizeIncomingBranches(incomingBranches));
      const hasProduction = normalized.some((b) => b.branch_type === 'production');

      queryClient.setQueryData<BranchesApiResponse>(['branches', projectId], (prev) => {
        const projectInfo = prev?.project;
        const availableBranches = prev?.available_branches ?? [];

        const updatedProject = {
          id: projectInfo?.id ?? Number(projectId),
          name: projectInfo?.name ?? '',
          github_repo: projectInfo?.github_repo ?? null,
          has_production_build:
            typeof projectInfo?.has_production_build === 'boolean'
              ? projectInfo.has_production_build
              : hasProduction,
        };

        return {
          success: true,
          branches_with_builds: normalized,
          available_branches: availableBranches,
          project: updatedProject,
        };
      });

      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'branch-history' &&
            key[1] === projectId
          );
        },
      });
    },
    [projectId, queryClient]
  );

  const handleBuildUpdate = useCallback(
    ({ branchId, build }: { branchId?: number; build?: BuildSocketShape }) => {
      if (!projectId || !branchId) return;

      const normalizedBuild = normalizeIncomingBuild(build);

      queryClient.setQueryData<BranchesApiResponse>(['branches', projectId], (prev) => {
        if (!prev) return prev;

        const updatedBranches = prev.branches_with_builds.map((branch) => {
          if (branch.id !== branchId || !normalizedBuild) {
            return branch;
          }

          return {
            ...branch,
            latest_build: {
              ...branch.latest_build,
              ...normalizedBuild,
            },
          };
        });

        return {
          ...prev,
          branches_with_builds: sortBranchesByLatest(updatedBranches),
        };
      });

      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === 'branch-history' &&
            key[1] === projectId &&
            key[2] === branchId
          );
        },
      });
    },
    [projectId, queryClient]
  );

  useDeploymentSocket({
    projectId: projectId || undefined,
    projectNumericId: data?.project?.id,
    onBranchesUpdate: handleBranchesUpdate,
    onBuildUpdate: handleBuildUpdate,
  });

  // Auto-select first branch when data loads
  const branches = data?.branches_with_builds || [];
  const selectedBranch = branches.find(b => b.id === selectedBranchId) || null;
  const githubRepo = data?.project?.github_repo || null;
  const hasProductionBranch = branches.some(b => b.branch_type === 'production');
  
  // Update URL when branch changes
  const setSelectedBranchId = useCallback((branchId: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      const currentBranchId = prev.get('branch');
      newParams.set('branch', branchId.toString());
      // Only reset tab to history if the branch actually changed
      if (currentBranchId !== branchId.toString()) {
        newParams.set('tab', 'history');
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // Update URL when tab changes
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // Auto-select first branch if none selected (or if selected branch doesn't exist)
  useEffect(() => {
    if (branches.length > 0) {
      const branchExists = selectedBranchId && branches.some(b => b.id === selectedBranchId);
      if (!branchExists) {
        // Set the first branch in URL without triggering tab reset
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('branch', branches[0].id.toString());
          if (!newParams.has('tab')) {
            newParams.set('tab', 'history');
          }
          return newParams;
        }, { replace: true });
      }
    }
  }, [selectedBranchId, branches, setSearchParams]);

  const handleDeploymentCreated = () => {
    refetch();
    // Also invalidate deployment form data cache
    queryClient.invalidateQueries({ queryKey: ['deploymentFormData', projectId] });
  };

  const openDeployModal = (branchType: 'production' | 'staging' | 'development' = 'production') => {
    const normalizedBranchType =
      branchType === 'production' && hasProductionBranch
        ? 'staging'
        : branchType;

    setDeployModalBranchType(normalizedBranchType);
    setIsDeployModalOpen(true);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load deployments. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      headerAction={
        <Button onClick={() => openDeployModal('production')}>
          <Plus className="h-4 w-4 mr-2" />
          Deploy Odoo Instance
        </Button>
      }
      noScroll
    >
      <div className="flex -mx-4 sm:-mx-6 lg:-mx-8 -my-6 h-[calc(100vh-7rem)]">
        {/* Branch Sidebar */}
        <BranchSidebar
          branches={branches}
          selectedBranchId={selectedBranchId}
          onSelectBranch={setSelectedBranchId}
          onAddDeployment={openDeployModal}
        />
        
        {/* Main Content */}
        <DeploymentContent 
          branch={selectedBranch} 
          projectId={projectId!}
          githubRepo={githubRepo}
          activeTab={activeTabFromUrl}
          onTabChange={setActiveTab}
        />
      </div>
      
      {/* Deploy Odoo Instance Modal */}
      <DeployInstanceModal
        open={isDeployModalOpen}
        onOpenChange={setIsDeployModalOpen}
        projectId={projectId!}
        onSuccess={handleDeploymentCreated}
        initialBranchType={deployModalBranchType}
      />
    </DashboardLayout>
  );
}
